import { useMemo } from 'react'

import {
  matchFilterBetweenDates, matchFilterKey, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ActivityModel, ActivityRequestOptions } from '@models/types'

const watchEntityKeys = ['activities']

export type ActivityFilters = {
  created_date_end?: string,
  created_date_start?: string,
  key?: string,
  limit?: number,
  recipient_id?: number,
  recipient_type?: string,
  trackable_only?: boolean,
}

type UseActivitiesOptions = {
  filters?: ActivityFilters,
  performHttpRequests?: boolean,
  requestOptions?: ActivityRequestOptions,
}

function useActivities(options: UseActivitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    created_date_end: filterCreatedDateEnd,
    created_date_start: filterCreatedDateStart,
    key: filterKey,
    limit: filterLimit,
    recipient_id: filterRecipientId,
    recipient_type: filterRecipientType,
    trackable_only: filterTrackableOnly,
  } = filters

  const {
    updatedEntities: { activities: activitiesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { activities } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredActivities = useMemo(() => {
    const filtered = Object.values(activities).filter((activity: ActivityModel) => {
      const {
        dates, key, recipient_id, recipient_type, trackable_id, trackable_type,
      } = activity

      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      const matchRecipientId = matchFilterNumber(recipient_id, filterRecipientId)
      const matchRecipientType = matchFilterKey(recipient_type, filterRecipientType)
      const matchRecipient = matchRecipientId && matchRecipientType

      // Backend matches against recipient or trackable
      const matchTrackableId = matchFilterNumber(trackable_id, filterRecipientId)
      const matchTrackableType = matchFilterKey(trackable_type, filterRecipientType)
      const matchTrackable = matchTrackableId && matchTrackableType

      // Match key
      const matchKey = matchFilterKey(key, filterKey)

      // Match dates
      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedDateStart, filterCreatedDateEnd)

      if (filterTrackableOnly){
        // when this is true we match only activities relating to the specific entity,
        // not the parent and the siblings of the entity
        return matchTrackable && matchCreatedAt && matchKey
      }
      return (matchRecipient || matchTrackable) && matchCreatedAt && matchKey
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [activitiesUpdatedAt, JSON.stringify(filters)])

  const filteredActivitiesCount = filteredActivities.length
  const hasFilteredActivities = !!filteredActivitiesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredActivitiesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingActivities } = useReduxAction(
    'activities',
    'loadActivities',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredActivities,
    filteredActivitiesCount,
    hasFilteredActivities,
    loading: loadingActivities,
  }
}

export type UseActivitiesPayload = ReturnType<typeof useActivities>

export default useActivities
